import { Component, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="user-menu-container">
      <button
        class="menu-trigger"
        (click)="toggleMenu()"
        [attr.aria-expanded]="isMenuOpen()"
        aria-label="Benutzermenü">
        <svg class="profile-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="8" r="4"></circle>
          <path d="M 6 20 Q 6 14 12 14 Q 18 14 18 20"></path>
        </svg>
      </button>

      @if (isMenuOpen()) {
        <div class="menu-dropdown">
          <button class="menu-item" (click)="onSubscriptionManagement()">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9h12M6 9a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2M6 9v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9M9 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2H9V5Z"></path>
            </svg>
            Abonnement-Verwaltung
          </button>
          <button class="menu-item" (click)="onChangePassword()">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="7" cy="12" r="4"></circle>
              <path d="M11 12h9M20 9v6"></path>
            </svg>
            Passwort ändern
          </button>
          <button class="menu-item logout-item" (click)="onLogout()">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <div class="logout-content">
              <span class="logout-text">Abmelden</span>
              <span class="logout-email">{{ userEmail() }}</span>
            </div>
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      --bright-blue: oklch(51.01% 0.274 263.83);
      --electric-violet: oklch(53.18% 0.28 296.97);
      --gray-900: oklch(19.37% 0.006 300.98);
      --gray-400: oklch(70.9% 0.015 304.04);
    }

    .user-menu-container {
      position: relative;
      display: flex;
      align-items: center;
      z-index: 1001;
    }

    .menu-trigger {
      background: linear-gradient(135deg, var(--bright-blue), var(--electric-violet));
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.3s ease;
      color: white;
      width: 40px;
      height: 40px;
      box-shadow: 0 2px 8px rgba(51, 65, 85, 0.15);
    }

    .menu-trigger:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 16px rgba(51, 65, 85, 0.25);
    }

    .menu-trigger:active {
      transform: scale(0.95);
    }

    .menu-trigger:focus {
      outline: 2px solid var(--bright-blue);
      outline-offset: 3px;
    }

    .profile-icon {
      width: 24px;
      height: 24px;
      color: white;
    }

    .menu-dropdown {
      position: absolute;
      top: 100%;
      right: 0;
      background: white;
      border: 1px solid var(--gray-400);
      border-radius: 0.375rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      min-width: 180px;
      z-index: 1001;
      margin-top: 0.5rem;
    }

    .menu-item {
      width: 100%;
      padding: 0.5rem 1rem;
      border: none;
      background: none;
      text-align: left;
      cursor: pointer;
      font-size: 0.8rem;
      color: #1e40af;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      white-space: nowrap;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f3f4f6;
      }

      &:hover svg {
        transform: scale(1.1);
        transition: transform 0.25s ease;
      }

      &:focus {
        outline: 2px solid var(--bright-blue);
        outline-offset: -2px;
      }

      &:first-child {
        border-radius: 0.375rem 0.375rem 0 0;
      }

      &:last-child {
        border-radius: 0 0 0.375rem 0.375rem;
      }

      .icon {
        display: inline-block;
        width: 1.25rem;
        text-align: center;
      }



      .logout-item {
        padding: 0.35rem 1rem;
      }

      .logout-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
      }

      .logout-text {
        font-size: 0.8rem;
        color: #1e40af;
        font-weight: 500;
      }

      .logout-email {
        font-size: 0.75rem;
        color: var(--bright-blue);
        font-weight: 400;
      }
    }

    @media (max-width: 768px) {
      .menu-item {
        white-space: normal;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'closeMenuOnClickOutside($event)'
  }
})
export class UserMenuComponent {
  private authService = inject(AuthService);
  private router = inject(Router);

  isMenuOpen = this.authService.userMenuOpen;
  userEmail = computed(() => this.authService.user()?.email || '');

  toggleMenu() {
    this.authService.userMenuOpen.update(open => !open);
  }

  onSubscriptionManagement() {
    this.authService.userMenuOpen.set(false);
    this.router.navigate(['/subscription-management']);
  }

  closeMenuOnClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu-container')) {
      this.authService.userMenuOpen.set(false);
    }
  }

  onChangePassword() {
    this.authService.userMenuOpen.set(false);
    this.router.navigate(['/change-password']);
  }

  onLogout() {
    this.authService.userMenuOpen.set(false);
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
